import React, { useEffect } from "react"
import { Box } from "@mui/material"
import { getTargets, SKIP_LINKS_SELECTORS, SKIP_LINKS_TITLES } from "./helpers"
import { SkipLink } from "./components"

interface Props {
  outterTargets?: [string, HTMLElement][]
}

const SKIP_LINKS_NAVIGATION = "SKIP_LINKS_NAVIGATION"

// a11y: resolve the skip target FRESH at activation time. The captured snapshot node can go
// STALE on the async test player — <main> is re-rendered as the test loads, leaving the
// snapshot pointing at a detached node — so re-query the selector on click and fall back to
// the captured node. (A target absent at render time is dropped by focusableTargets below and
// reappears on a later render once it exists; cold-load skip-to-main verified on the test player.)
const resolveTarget = (
  targetType: string,
  captured: HTMLElement
): HTMLElement | null => {
  const selector =
    SKIP_LINKS_SELECTORS[targetType as keyof typeof SKIP_LINKS_SELECTORS]
  if (!selector) return captured
  const fresh =
    selector.type === "id"
      ? document.getElementById(selector.value)
      : document.querySelector<HTMLElement>(selector.value)
  return fresh ?? captured
}

const SkipLinks: React.FC<Props> = ({ outterTargets }) => {
  const skipLinkIds = Object.entries(SKIP_LINKS_SELECTORS)
  const targets = outterTargets ?? getTargets(skipLinkIds)

  const focusableTargets = targets.filter(
    ([targetType, target]) => !!targetType && !!target
  )

  const handleSkipLinks = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return
    const activeElement = document.activeElement as HTMLElement | null
    // a11y: only dismiss a focused skip link on Escape. Blurring the active
    // element unconditionally stole focus that a dialog/drawer/menu had just
    // restored to its opener on the same Escape press (focus fell to <body>).
    if (
      activeElement?.closest("[data-automation-id='skip-links-navigation']")
    ) {
      activeElement.blur()
    }
  }

  useEffect(() => {
    window.addEventListener("keyup", handleSkipLinks)

    return () => {
      window.removeEventListener("keyup", handleSkipLinks)
    }
  }, [])

  return !!focusableTargets.length ? (
    <Box
      id={SKIP_LINKS_NAVIGATION}
      component="nav"
      data-automation-id="skip-links-navigation"
    >
      <Box m={0} p={0} component="ul">
        {focusableTargets.map(([targetType, target]) => (
          <SkipLink
            key={targetType}
            onClick={() => resolveTarget(targetType, target)?.focus()}
            onKeyUp={e =>
              e.key === "Enter" && resolveTarget(targetType, target)?.focus()
            }
          >
            {SKIP_LINKS_TITLES[targetType]}
          </SkipLink>
        ))}
      </Box>
    </Box>
  ) : null
}

export default SkipLinks
